import { ObjectId, NodeState } from './engine.nodes.state.model';
import { BdGraph, BdGraphNode } from 'src/lib/graph/graph.model';
import { PaintPresetOriginal } from '../../models/paint-presets.const';
import { ColorRecipeModel } from 'src/app/edit/models/color-recipe.model';
import { EngineColorRecipeModel } from 'src/app/edit/models/engine-color-recipe.model';
import { GraphUtils } from 'src/lib/graph/graph-utils';

export function hasShownSuccessor(graph, objectId, showObjects): boolean {
  // get successors

  const successors = GraphUtils.getSuccessors(graph, objectId);

  // are there successors?
  if (successors.length === 0) {
    return false;
  }

  // are any of these successors in show objects as true?
  for (const successor of successors) {
    if (showObjects[successor] === true) {
      return true;
    }
  }

  // for each successor, recurse
  return successors.some((successor) => hasShownSuccessor(graph, successor, showObjects));
}

export function hasShownAncestor(graph, objectId, showObjects): boolean {
  // is this object in showObjects?

  switch (showObjects[objectId]) {
    case true:
      return true;
    case false:
      return false;
    case undefined: {
      // this object is not in show objects.
      // recurse until we find a parent
      const parent = GraphUtils.getAncestor(graph, objectId);
      if (!parent) {
        return false;
      }
      return hasShownAncestor(graph, parent, showObjects);
    }
  }
}

function isObjectPickable(graph, objectId, pickThroughObjects): boolean {
  switch (pickThroughObjects[objectId]) {
    case true:
      return false;
    case false:
      return true;
    case undefined: {
      // this object is not in show objects.
      // recurse until we find a parent
      const parent = GraphUtils.getAncestor(graph, objectId);
      if (!parent) {
        return true;
      }
      return isObjectPickable(graph, parent, pickThroughObjects);
    }
  }
}

function syncHasColorInfo(syncForObject) {
  return (
    'tintColor' in syncForObject ||
    'opacity' in syncForObject ||
    'brightness' in syncForObject ||
    'saturation' in syncForObject ||
    'contrast' in syncForObject
  );
}

function getObjectPaint(graph, objectId, sync): ColorRecipeModel {
  if (sync && sync[objectId] && syncHasColorInfo(sync[objectId])) {
    return ColorRecipeModel.fromEngine(new EngineColorRecipeModel(sync[objectId]));
  }
  const parent = GraphUtils.getAncestor(graph, objectId);
  return parent ? getObjectPaint(graph, parent, sync) : PaintPresetOriginal;
}

// build node state:

// use chapter's showObjects to decide if an object is shown or hidden
// if undefined, defer to parent's value.  keep recursing until we find a value.

// use chapter's pickThroughObjects to decide if an object is pickable or not
// if undefined, defer to parent's value.  keep recursing until we find a value.

// use chapter's synchronization to decide an object's color properties.
// if undefined, defer to parent's value.  keep recursing until we find a value.

export function buildNodeState(
  graph: BdGraph<BdGraphNode>,
  showObjects: Record<ObjectId, boolean>,
  pickThroughObjects: Record<ObjectId, boolean>,
  sync: Record<ObjectId, EngineColorRecipeModel>,
): Record<ObjectId, NodeState> {
  function nodeFactory(objectId): NodeState {
    const shown =
      hasShownAncestor(graph, objectId, showObjects) ||
      hasShownSuccessor(graph, objectId, showObjects);
    const pickable = isObjectPickable(graph, objectId, pickThroughObjects);
    const paint = getObjectPaint(graph, objectId, sync);
    return {
      objectId,
      checked: false,
      selected: false,
      paint,
      pickable,
      shown,
      shownSetByTween: false,
    };
  }

  const nodes = Object.keys(graph.nodes).reduce((nodeState, objectId) => {
    nodeState[objectId] = nodeFactory(objectId);
    return nodeState;
  }, {});

  return nodes;
}
